import { Image } from 'antd';
import clsx from 'clsx';
import {
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FileIcon as ReactFileIcon,
  defaultStyles,
  type FileIconProps as ReactFileIconProps,
} from 'react-file-icon';
import { iconComponents } from '../../../components/Core/IconImports';
import { useFilesImagePreviewContext } from '../../../hooks/useFilesImagePreviewSrc';
import { useFileThumbnail } from '../../../hooks/useFileThumbnail';
import type { SecretFilePresetType } from '../../../interfaces/FileSecret';
import { useAppStore } from '../../../store/store';
import {
  getBrandStyleForExtension,
  getLabelColorForExtension,
} from '../../../utils/fileIconColors';
import { FILES_IMAGE_PREVIEW_FILE_ID_ATTR } from '../../../utils/filePreview';
import { getSecretPresetIconName } from '../../../utils/fileSecretPresets';
import {
  isFileThumbnailSupported,
  isImageFileExtension,
  isSecretExtension,
} from '../../../utils/utils';
import { Icon } from '../../Core/Icon';

/**
 * Converts a file extension to lowercase and ensures a leading dot.
 * This normalization allows for consistent comparison and lookup.
 * @param ext The file extension string.
 * @returns Normalized file extension string.
 */
function normalizeExtension(ext: string): string {
  if (!ext) return '';
  const lower = ext.toLowerCase();
  return lower.startsWith('.') ? lower : `.${lower}`;
}

/** Internal extensions whose label must not appear on the file glyph. */
const HIDDEN_FILE_EXTENSION_LABELS = new Set(['fgsecret', 'fgnote']);

/**
 * Props for the FileIcon component. Describes file/folder identity and presentation options.
 */
interface FileIconProps {
  fileExtension: string;
  fileId?: number;
  isFolder?: boolean;
  isFolderPasswordProtected?: boolean;
  isFolderPasswordBypassed?: boolean;
  isShortcut?: boolean;
  isAiGrant?: boolean;
  /**
   * `.fgsecret` preset from file content / list DTO when BE exposes it.
   * Centers New-menu Lucide mark on classic react-file-icon; omit → FileKeyIcon.
   */
  secretPreset?: SecretFilePresetType | string | null;
  /**
   * If non-empty, only these extensions get a thumbnail preview.
   * If empty, uses the full list from isFileThumbnailSupported.
   */
  allowedFilePreviewExtensions?: string[];
  /** Enables Ant Design image preview and registers the thumbnail with the parent preview group. */
  enableImagePreview?: boolean;
  /** @deprecated HD is applied by FilesImagePreviewGroup imageRender; keep thumbnail as preview.src. */
  fullPreviewSrc?: string;
  style?: CSSProperties | null;
  className?: string;
  iconClassName?: string;
  imageClassName?: string;
  shortcutIconClassName?: string;
  onClick?: () => void;
}

/**
 * FileIcon renders an icon or thumbnail representing a file or folder.
 * Displays a thumbnail when available, and icons based on extension, folder/password, AI grants, and shortcuts.
 */
const FileIcon: FC<FileIconProps> = ({
  fileExtension,
  fileId,
  isFolder = false,
  isFolderPasswordProtected = false,
  isFolderPasswordBypassed = false,
  isShortcut = false,
  isAiGrant = false,
  secretPreset = null,
  allowedFilePreviewExtensions = [],
  enableImagePreview = false,
  fullPreviewSrc: _fullPreviewSrc,
  style = null,
  className = '',
  iconClassName = '',
  imageClassName = '',
  shortcutIconClassName = '',
  onClick,
}) => {
  /**
   * Determines if thumbnail preview is supported for this file type and extension:
   * - Never fetch for folders or when fileId is missing.
   * - If allowedFilePreviewExtensions is empty, use isFileThumbnailSupported's full list.
   * - Otherwise only allow specified extensions.
   */
  const isSupported = useMemo(() => {
    if (isFolder || !fileId) return false;
    const normalizedExt = normalizeExtension(fileExtension);
    if (allowedFilePreviewExtensions.length === 0) {
      return isFileThumbnailSupported(fileExtension);
    }
    const normalizedAllowed = allowedFilePreviewExtensions.map(normalizeExtension);
    return normalizedAllowed.includes(normalizedExt);
  }, [isFolder, fileId, fileExtension, allowedFilePreviewExtensions]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  /**
   * Defers thumbnail fetch until the icon is near the viewport.
   * Disconnects after the first intersection to avoid repeated observation.
   */
  useEffect(() => {
    if (!isSupported || !containerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isSupported, fileId]);

  /**
   * Fetch file thumbnail from API (if supported and visible).
   * If not visible or unsupported, skips API call.
   */
  const { fileThumbnailUrl } = useFileThumbnail(
    isSupported && isVisible ? fileId : null,
    fileExtension,
  );
  const previewContext = useFilesImagePreviewContext();

  const [fileThumbnailError, setFileThumbnailError] = useState<boolean>(false);

  /**
   * Should a file thumbnail image be shown?
   * Only true if supported type, we have a thumbnail URL, and no load error.
   */
  const shouldShowFileThumbnail = isSupported && !!fileThumbnailUrl && !fileThumbnailError;
  /**
   * Carousel preview is image-only. Keep `preview.src` as the thumbnail URL so it stays
   * matched to PreviewGroup `items` after full-resolution fetch (HD is applied in imageRender).
   */
  const shouldEnableImagePreview =
    enableImagePreview &&
    shouldShowFileThumbnail &&
    !!fileThumbnailUrl &&
    isImageFileExtension(fileExtension);

  useEffect(() => {
    if (!shouldEnableImagePreview || fileId == null || !fileThumbnailUrl) return;
    previewContext?.registerPreviewableImage(fileId, fileThumbnailUrl);
  }, [shouldEnableImagePreview, fileId, fileThumbnailUrl, previewContext]);

  /**
   * Hooks into app store for password session.
   * Used to display correct folder (locked/unlocked/default) icon.
   */
  const filePasswordSessions = useAppStore((s) => s.filePasswordSessions);
  const getFilePasswordSession = useAppStore((s) => s.getFilePasswordSession);

  /**
   * Determines which folder icon should display:
   * - Default folder
   * - Locked folder (password protection)
   * - Unlocked folder (password session present or password bypassed)
   */
  const folderIconName = useMemo(() => {
    if (!isFolder) return null;
    if (!isFolderPasswordProtected) return 'FolderFilledIcon';
    const isUnlocked = fileId != null && !!getFilePasswordSession(fileId);
    return isUnlocked || isFolderPasswordBypassed
      ? 'FolderFilledLockOpenIcon'
      : 'FolderFilledLockIcon';
  }, [
    isFolder,
    isFolderPasswordProtected,
    fileId,
    isFolderPasswordBypassed,
    filePasswordSessions, // Ensures re-render on session change
    getFilePasswordSession,
  ]);

  /**
   * AI Grant styling: remove color prop to let gradient/AI style through,
   * otherwise use provided style.
   */
  const finalStyle = isAiGrant ? { ...(style || {}), color: undefined } : style || undefined;

  const fileExtKey = useMemo(
    () => normalizeExtension(fileExtension).replace(/^\./, '') || 'file',
    [fileExtension],
  );
  const brandStyle = useMemo(() => getBrandStyleForExtension(fileExtKey), [fileExtKey]);
  const fileIconStyle = useMemo(
    () =>
      brandStyle ??
      (fileExtKey in defaultStyles
        ? (defaultStyles as Record<string, Partial<ReactFileIconProps>>)[fileExtKey]
        : undefined),
    [brandStyle, fileExtKey],
  );

  /**
   * Use built-in colors when we have brand style or package defaultStyles with color/labelColor.
   * Otherwise use our pastel palette.
   */
  const hasBuiltInColors =
    !!fileIconStyle &&
    (typeof fileIconStyle.labelColor === 'string' || typeof fileIconStyle.color === 'string');

  // Show at most 3 chars extension in the icon label (e.g., "doc…" for long types).
  const MAX_EXT_LABEL_LENGTH = 3;
  const labelExtension = HIDDEN_FILE_EXTENSION_LABELS.has(fileExtKey)
    ? ''
    : fileExtKey.length > MAX_EXT_LABEL_LENGTH
      ? `${fileExtKey.slice(0, MAX_EXT_LABEL_LENGTH)}…`
      : fileExtKey;

  const glyphType = fileIconStyle?.type ?? 'settings';

  const isSecretFileIcon = isSecretExtension(fileExtension);
  const secretIconName = useMemo(
    () => (isSecretFileIcon ? getSecretPresetIconName(secretPreset) : null),
    [isSecretFileIcon, secretPreset],
  );

  /**
   * If file changes or a new thumbnail loads, reset thumbnail error state.
   */
  useEffect(() => {
    setFileThumbnailError(false);
  }, [fileId, fileThumbnailUrl]);

  const handleContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative flex items-center justify-center',
        isFolder && 'text-yellow', // Default folder color (--color-yellow)
        isAiGrant && 'fill-ai-gradient', // AI grant colored gradient
        className,
      )}
      style={finalStyle}
      {...(onClick
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick,
            onKeyDown: handleContainerKeyDown,
          }
        : {})}
    >
      {/* Shortcut badge blob (e.g., alias/links indicator) */}
      {isShortcut && (
        <Icon
          name="ExternalLinkIcon"
          className={clsx(
            'text-blue-dark absolute',
            shortcutIconClassName ? shortcutIconClassName : 'bottom-[15%] left-1 h-3 w-3',
          )}
        />
      )}

      {shouldShowFileThumbnail && fileThumbnailUrl ? (
        <div
          className={clsx(
            'border-gray-light relative overflow-hidden rounded-md border',
            imageClassName ? imageClassName : clsx('aspect-square', className),
          )}
          {...(shouldEnableImagePreview && fileId != null
            ? { [FILES_IMAGE_PREVIEW_FILE_ID_ATTR]: String(fileId) }
            : {})}
          style={{
            height: '100%',
            width: '100%',
          }}
        >
          <Image
            src={fileThumbnailUrl}
            alt=""
            rootClassName="h-full! w-full! [&_.ant-image]:h-full! [&_.ant-image]:w-full! [&_.ant-image-img]:h-full! [&_.ant-image-img]:w-full! [&_.ant-image-img]:object-cover!"
            className="relative h-full! w-full! object-cover!"
            preview={shouldEnableImagePreview ? { src: fileThumbnailUrl } : false}
            loading="lazy"
            onClick={shouldEnableImagePreview ? (event) => event.stopPropagation() : undefined}
            onError={() => setFileThumbnailError(true)}
            style={{
              height: '100%',
              objectFit: 'cover',
              width: '100%',
            }}
          />
        </div>
      ) : isFolder ? (
        // Folder icon (locked, unlocked, standard)
        <Icon
          name={folderIconName as keyof typeof iconComponents}
          className={clsx('h-full w-full', iconClassName)}
        />
      ) : (
        // File: classic react-file-icon silhouette; secrets overlay New-menu mark (no .fgsecret label)
        <div
          className={clsx('relative flex h-full w-full items-center justify-center', iconClassName)}
        >
          <ReactFileIcon
            extension={labelExtension}
            {...(fileIconStyle ?? {})}
            type={glyphType}
            {...(hasBuiltInColors
              ? {}
              : {
                  color: '#f8f9fb',
                  labelColor: getLabelColorForExtension(fileExtKey),
                  labelTextColor: '#fff',
                  glyphColor: '#939393',
                })}
            {...(secretIconName
              ? {
                  // Hide package glyph — Lucide center mark replaces it
                  glyphColor: 'transparent',
                }
              : {})}
          />
          {secretIconName ? (
            <Icon
              name={secretIconName}
              className="text-gray-dark pointer-events-none absolute top-[42%] left-1/2 h-[42%] w-[42%] -translate-x-1/2 -translate-y-1/2"
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FileIcon;
